import { defineStore } from "pinia";
import Flipper from "@/modules/flipper";
import { ProcessingState, UploadState, type Image } from "@/modules/internal_api/image";

import { DrawAction } from "@/stores/line";
import type { Line, Point } from "@/stores/line";
import {
  rbgEditorBackgroundsPanelOpenedV100,
  rbgEditorEffectsPanelOpenedV100,
  rbgEditorEraseRestorePanelOpenedV100,
  rbgEditorAdjustPanelOpenedV100,
  rbgEditorDesignPanelOpenedV100,
} from "kaleido-event-tracker";
import { BackgroundImageCollection, BackgroundImageSearchResult } from "@/modules/internal_api/background_search";
import { MagicBackgroundTemplateResult } from "@/modules/internal_api/magic_backgrounds";
import { AddBackgroundTabs } from "@/components/upload/drawer_add_background.vue";
import { FlashAlertData, FlashAlerts } from "@/modules/flash_alerts";

export enum DrawMode {
  Magic = "magic",
  Classic = "classic",
}

export enum BrushColor {
  Erase = "#DB1436",
  Restore = "#C1E7C2",
}

export enum ViewMode {
  Before = "before",
  After = "after",
}

export type MagicBrushCache = {
  formData: FormData;
};

export type DrawSettings = {
  isEnabled: boolean;
  action: DrawAction;
  mode: DrawMode;
  color: BrushColor;
  brushSize: number;
  magicBrushSize: number;
};

export type BackgroundsSearch = {
  searchTerm: string;
  page: number;
  searchResults: BackgroundImageSearchResult[];
  collections: BackgroundImageCollection[];
  selectedCollection: BackgroundImageCollection | null;
};

export type Panel = "fx" | "addBackground" | "eraseRestore" | "adjust" | "design" | null;

type UseNullStore = ReturnType<typeof defineStore>;
type NullStore = ReturnType<UseNullStore>;
export type EditorStore = ReturnType<typeof useEditorStore>;
export type EditorStoreSGA = Omit<EditorStore, keyof NullStore>;

interface State {
  images: Image[];
  selectedImageId: string | null;
  lines: Line[];
  drawSettings: DrawSettings;
  backgroundSamplesCollectionIds: string[];
  backgroundColorSamples: string[];
  viewMode: ViewMode;
  isMagicBrushEnabled: boolean;
  magicBrushCache: MagicBrushCache | null;
  manualBrushCursorPosition: Point;
  magicBrushCursorPosition: Point;
  isSwitchingImages: boolean;
  isHoveringEditor: boolean;
  isHoveringZoomButton: boolean;
  isAdjustingBrushSize: boolean;
  isUploadingCustomBackground: boolean;
  isApplyingBackgroundChanges: boolean;
  openedPanel: Panel;
  openedBackgroundTab: AddBackgroundTabs;
  imagesSearch: Map<string, BackgroundsSearch>;
  isSearchActive: boolean;
  isProcessingPreviewDownload: boolean;
  isProcessingHdDownload: boolean;
  isProcessingMagicBackgroundHdDownload: boolean;
  creditsBalance: number;
  flashAlerts: FlashAlerts;
  magicBackgrounds: {
    inspirations: MagicBackgroundTemplateResult;
    isDownloadingHD: boolean;
  };
}

export const useEditorStore = defineStore("editor", {
  state: (): State => ({
    images: [],
    selectedImageId: null,
    lines: [],
    drawSettings: {
      isEnabled: false,
      action: DrawAction.Erase,
      mode: DrawMode.Magic,
      color: BrushColor.Erase,
      brushSize: 10,
      magicBrushSize: 50,
    },
    backgroundSamplesCollectionIds: [],
    backgroundColorSamples: [],
    isMagicBrushEnabled: true,
    magicBrushCache: null,
    viewMode: ViewMode.After,
    manualBrushCursorPosition: { x: 0, y: 0 },
    magicBrushCursorPosition: { x: 0, y: 0 },
    isSwitchingImages: false,
    isHoveringEditor: false,
    isHoveringZoomButton: false,
    isAdjustingBrushSize: false,
    isUploadingCustomBackground: false,
    isApplyingBackgroundChanges: false,
    openedPanel: null,
    openedBackgroundTab: "ai_scene",
    imagesSearch: new Map<string, BackgroundsSearch>(),
    isSearchActive: false,
    isProcessingPreviewDownload: false,
    isProcessingHdDownload: false,
    isProcessingMagicBackgroundHdDownload: false,
    creditsBalance: 0,
    magicBackgrounds: {
      inspirations: {
        product: [],
        person: [],
        graphic: [],
        transportation: [],
        animal: [],
        car: [],
        other: [],
      },
      isDownloadingHD: false,
    },
    errorAlert: {
      isVisible: false,
      message: "",
      timestamp: 0,
    },
    flashAlerts: new Map<string, FlashAlertData>(),
  }),

  getters: {
    selectedImage(state: State): Image | null {
      if (!state.selectedImageId) {
        return null;
      }

      return this.images.find((image: Image) => image.meta.id === state.selectedImageId);
    },
    lastLine(state: State): Line | null {
      if (state.lines.length === 0) {
        return null;
      }

      return state.lines[state.lines.length - 1];
    },
    collections(state: State): BackgroundImageCollection[] {
      return state.imagesSearch.get(this.selectedImageId)?.collections || [];
    },
    selectedCollection(state: State): BackgroundImageCollection | null {
      return state.imagesSearch.get(this.selectedImageId)?.selectedCollection;
    },
    isCollectionSelected(state: State): boolean {
      if (!state.imagesSearch.has(this.selectedImageId)) {
        return false;
      }

      return state.imagesSearch.get(this.selectedImageId)?.selectedCollection !== null;
    },
    backgroundsSearchResults(state: State): BackgroundImageSearchResult[] {
      return state.imagesSearch.get(this.selectedImageId)?.searchResults || [];
    },
    searchTerm(state: State): string {
      return state.imagesSearch.get(this.selectedImageId)?.searchTerm || "";
    },
    currentSearchResultsPage(state: State): number {
      return state.imagesSearch.get(this.selectedImageId)?.page || 1;
    },
    isBackgroundSearchActive(state: State): boolean {
      return state.isSearchActive;
    },
    isSearchTermEmpty(state: State): boolean {
      return state.imagesSearch.get(this.selectedImageId)?.searchTerm === "";
    },
    isMagicBrushPreviewProcessing(): boolean {
      return this.selectedImage?.magicBrushPreviewResult?.state === ProcessingState.Processing;
    },
    isPreviewProcessing(): boolean {
      return this.selectedImage?.previewResult?.state === ProcessingState.Processing;
    },
    inspirations(state: State): MagicBackgroundTemplateResult {
      return state.magicBackgrounds.inspirations;
    },
    hasError(): boolean {
      return this.imageHasError(this.selectedImage);
    },
  },

  actions: {
    // Replace the entire list of images
    // This is used when first mounting the editor
    // Also selects the first image in the list
    setImages(images: Image[]): void {
      this.images = images;
      if (images.length > 0) {
        this.selectImage(images[0]);
      }
    },

    // Updates the selected image
    selectImage(image: Image): void {
      const index = this.images.findIndex((i: Image) => i.meta.id === image.meta.id);
      if (index < 0) return;

      this.selectedImageId = image.meta.id;
    },

    // Add an image to the beginning of the list
    // This is used when the user uploads a new image
    addImage(image: Image): void {
      this.images.unshift(image);
    },

    // Update an image in place, usually after polling for processing results
    updateImage(image: Image): void {
      const index = this.images.findIndex((i: Image) => i.meta.id === image.meta.id);

      this.images.splice(index, 1, image);
    },

    // Replace an image with another one
    // This is used when replacing a temporary/local image with a created record from the server
    replaceImage(oldImage: Image, newImage: Image): void {
      const index = this.images.findIndex((i: Image) => i.meta.id === oldImage.meta.id);

      // At this stage the original image is uploaded but has not yet been processed
      // so we do not now the dimensions of the uploade image, for preview reasons we did however
      // already determine the most likely preview resolution, so we can copy those over here
      const { previewWidth, previewHeight, preview, livePreview, width, height, sizeMb } = oldImage.meta;
      newImage.meta = { ...newImage.meta, previewWidth, previewHeight, preview, livePreview, width, height, sizeMb };

      this.images.splice(index, 1, newImage);
    },

    // Remove an image from the local store
    // Does not delete the image from the server (use Client.deleteImage for that)
    deleteImage(image: Image): void {
      const index = this.images.findIndex((i: Image) => i.meta.id === image.meta.id);
      const images = this.images.filter((i: Image) => i.meta.id !== image.meta.id);
      this.images = images;

      if (images.length === 0) {
        this.selectedImageId = null;
        return;
      }

      let newIndex = 0;
      if (index > 0) {
        newIndex = index - 1;
      }

      // If the deleted image was selected, select the first image in the list
      if (this.selectedImageId === image.meta.id) {
        this.selectImage(this.images[newIndex]);
      }
    },

    enterEraseMode(): void {
      this.clearLines();
      this.drawSettings.action = DrawAction.Erase;
      this.drawSettings.color = BrushColor.Erase;
    },

    enterRestoreMode(): void {
      this.clearLines();
      this.drawSettings.action = DrawAction.Restore;
      this.drawSettings.color = BrushColor.Restore;
    },

    startLine(point: Point): void {
      // A line consists of at least two points so we need to move the
      // starting point by one pixel to create a valid line
      const endPoint = { x: point.x + 1, y: point.y + 1 };
      this.lines.push({ points: [point, endPoint] });
    },

    // Add a point to the last/current line
    addLinePoint(point: Point): void {
      const lastLine = this.lastLine;
      if (lastLine) {
        lastLine.points.push(point);
      }
      this.lines.splice(this.lines.length - 1, 1, lastLine);
    },

    togglePanel(id: Panel) {
      this.openedPanel = this.openedPanel === id ? null : id;

      switch (this.openedPanel) {
        case "addBackground":
          rbgEditorBackgroundsPanelOpenedV100({ image_id: this.selectedImageId });
          return;
        case "eraseRestore":
          rbgEditorEraseRestorePanelOpenedV100({ image_id: this.selectedImageId });
          return;
        case "fx":
          rbgEditorEffectsPanelOpenedV100({ image_id: this.selectedImageId });
          return;
        case "adjust":
          rbgEditorAdjustPanelOpenedV100({ image_id: this.selectedImageId });
          return;
        case "design":
          rbgEditorDesignPanelOpenedV100({ image_id: this.selectedImageId });
          return;
        default:
          return;
      }
    },

    clearLines(): void {
      this.lines = [];
    },

    closePanel(): void {
      this.openedPanel = null;
    },

    initSearch(): void {
      if (!this.imagesSearch.get(this.selectedImageId)) {
        const newSearch: BackgroundsSearch = {
          searchTerm: "",
          page: 1,
          searchResults: [],
          collections: [],
          selectedCollection: null,
        };
        this.imagesSearch.set(this.selectedImageId, newSearch);
      }
    },

    startSearch(searchTerm: string, results: BackgroundImageSearchResult[]): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        searchTerm,
        searchResults: results,
        selectedCollection: null,
        page: 1,
      };

      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    clearSearchResults(): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        searchResults: [],
        selectedCollection: null,
        page: 1,
      };

      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    turnSearchOn(): void {
      this.isSearchActive = true;
    },

    turnSearchOff(): void {
      this.isSearchActive = false;
    },

    clearSearchTerm(): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        searchTerm: "",
        searchResults: [],
        page: 1,
      };

      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    loadCollections(collections: BackgroundImageCollection[]): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        collections,
      };

      // TODO discuss - loading collections per image should not be necessary, they can be loaded once and shared
      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    selectCollection(collection: BackgroundImageCollection): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        selectedCollection: collection,
        searchTerm: "",
      };

      this.turnSearchOn();
      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    clearCollectionResults(): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        selectedCollection: null,
        searchResults: [],
        page: 1,
      };

      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    loadMoreSearchResults(results: BackgroundImageSearchResult[], currentPage: number): void {
      const updatedSearchResults: BackgroundsSearch = {
        ...this.imagesSearch.get(this.selectedImageId),
        searchResults: this.imagesSearch.get(this.selectedImageId).searchResults.concat(results),
        page: currentPage,
      };

      this.imagesSearch.set(this.selectedImageId, updatedSearchResults);
    },

    updateCreditsBalance(credits: number): void {
      if (credits === null || credits === undefined) return;

      // Credits could be zero as well.
      this.creditsBalance = credits;
    },

    loadInspirations(inspirations: MagicBackgroundTemplateResult): void {
      this.magicBackgrounds.inspirations = inspirations;
    },

    useNewEditor(withinMonitorSize: Boolean): Boolean {
      return Flipper.isEnabled("new_photo_editor") && !withinMonitorSize;
    },
    imageHasError(image: Image): boolean {
      if (!image) return false;

      const originalCheck = image.original.state === UploadState.Error;
      const previewCheck = image.previewResult?.state === ProcessingState.Error;

      return originalCheck || previewCheck;
    },

    deleteErroredImage(image: Image): void {
      if (!image || !this.imageHasError(image)) return;

      this.deleteImage(image);
    },
  },
});
